// src/components/features/content/ContentAnalytics.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardContent,
  CardHeader,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
  DonutLarge as DonutIcon,
  PieChart as PieChartIcon, 
  ShowChart as LineChartIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';

// Import custom components
import { Card, MagicLoader } from '../../../design-system';

// Import hooks
import useContentStore from '../../../stores/contentStore';
import useNotificationStore from '../../../stores/notificationStore';
import ContentTypeIcon from '../../ui/ContentTypeIcon';
import { EntityStatusBadge } from '../../core';

// Tab Panel Component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon, change, color }) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
            
            {change !== undefined && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: change >= 0 ? 'success.main' : 'error.main',
                  display: 'flex',
                  alignItems: 'center',
                  mt: 1
                }}
              >
                {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
              </Typography>
            )}
          </Box>
          
          {icon && (
            <Box 
              sx={{ 
                p: 1, 
                borderRadius: '50%', 
                bgcolor: alpha(color || theme.palette.primary.main, 0.1),
                color: color || theme.palette.primary.main
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Daily Engagement Line Chart Component
const DailyEngagementChart = ({ data }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="engagement" 
            stroke={theme.palette.primary.main} 
            activeDot={{ r: 8 }} 
          />
          <Line 
            type="monotone" 
            dataKey="reach" 
            stroke={theme.palette.secondary.main} 
          />
          <Line 
            type="monotone" 
            dataKey="impressions" 
            stroke={theme.palette.info.main} 
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Platform Distribution Pie Chart Component
const PlatformDistributionChart = ({ data }) => {
  const theme = useTheme();
  
  // Colors for platforms
  const PLATFORM_COLORS = {
    facebook: '#1877F2',
    instagram: '#E1306C',
    twitter: '#1DA1F2',
    linkedin: '#0077B5',
    youtube: '#FF0000',
    tiktok: '#000000',
    snapchat: '#FFFC00',
  };
  
  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={true}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={PLATFORM_COLORS[entry.name.toLowerCase()] || theme.palette.primary.main}
              />
            ))}
          </Pie>
          <Tooltip formatter={(value) => value.toLocaleString()} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Engagement Metrics Radar Chart Component
const EngagementRadarChart = ({ data }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
          <PolarGrid />
          <PolarAngleAxis dataKey="metric" />
          <PolarRadiusAxis />
          <Radar 
            name="Current Content" 
            dataKey="value" 
            stroke={theme.palette.primary.main} 
            fill={theme.palette.primary.main} 
            fillOpacity={0.6} 
          />
          <Radar 
            name="Average" 
            dataKey="average" 
            stroke={theme.palette.secondary.main} 
            fill={theme.palette.secondary.main} 
            fillOpacity={0.6} 
          />
          <Legend />
          <Tooltip />
        </RadarChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Demographic Bar Chart Component
const DemographicBarChart = ({ data }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="age" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar 
            dataKey="male" 
            name="Male" 
            fill={theme.palette.primary.main} 
          />
          <Bar 
            dataKey="female" 
            name="Female" 
            fill={theme.palette.secondary.main} 
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Content Analytics Component
const ContentAnalytics = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    currentContent,
    isLoading,
    error,
    fetchContentById,
    getContentAnalytics,
    clearCurrentContent,
    clearError
  } = useContentStore();
  
  const { showError } = useNotificationStore();
  
  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('week');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  
  // Load content on mount
  useEffect(() => {
    loadContent();
    
    return () => {
      clearCurrentContent();
    };
  }, [id]);
  
  // Load analytics data when content is loaded or time range changes
  useEffect(() => {
    if (currentContent) {
      loadAnalytics();
    }
  }, [currentContent, timeRange]);
  
  // Load content
  const loadContent = async () => {
    try {
      await fetchContentById(id);
    } catch (error) {
      showError('Failed to load content: ' + error.message);
    }
  };
  
  // Load analytics
  const loadAnalytics = async () => {
    setIsLoadingAnalytics(true);
    
    try {
      const response = await getContentAnalytics(id, { timeRange });
      setAnalyticsData(response.data);
    } catch (error) {
      showError('Failed to load analytics: ' + error.message);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle time range change
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  // Get content title
  const getContentTitle = () => {
    return currentContent?.title || 'Content';
  };
  
  // Generate sample data for demo purposes
  // Note: Replace this with actual data in production
  const generateSampleData = () => {
    // Sample data for demo purposes
    const sampleData = {
      metrics: {
        impressions: {
          total: 12548,
          change: 12.5
        },
        reach: {
          total: 8752,
          change: 8.2
        },
        engagement: {
          total: 2341,
          change: 15.7
        },
        clicks: {
          total: 876,
          change: -2.3
        },
        shares: {
          total: 352,
          change: 28.4
        },
        comments: {
          total: 124,
          change: 5.1
        },
        engagementRate: {
          total: 4.8,
          change: 1.2
        }
      },
      dailyTrend: [
        { date: '10/01', impressions: 1500, reach: 1200, engagement: 300 },
        { date: '10/02', impressions: 1620, reach: 1350, engagement: 320 },
        { date: '10/03', impressions: 1750, reach: 1400, engagement: 350 },
        { date: '10/04', impressions: 1800, reach: 1500, engagement: 380 },
        { date: '10/05', impressions: 1950, reach: 1600, engagement: 420 },
        { date: '10/06', impressions: 2100, reach: 1750, engagement: 450 },
        { date: '10/07', impressions: 1828, reach: 1452, engagement: 421 },
      ],
      platformDistribution: [
        { name: 'Instagram', value: 45 },
        { name: 'Facebook', value: 30 },
        { name: 'Twitter', value: 15 },
        { name: 'LinkedIn', value: 10 }
      ],
      engagementRadar: [
        { metric: 'Likes', value: 85, average: 75 },
        { metric: 'Comments', value: 65, average: 60 },
        { metric: 'Shares', value: 70, average: 50 },
        { metric: 'Saves', value: 55, average: 40 },
        { metric: 'Clicks', value: 90, average: 65 }
      ],
      demographics: [
        { age: '18-24', male: 650, female: 780 },
        { age: '25-34', male: 950, female: 1120 },
        { age: '35-44', male: 720, female: 680 },
        { age: '45-54', male: 440, female: 360 },
        { age: '55+', male: 250, female: 180 }
      ]
    };
    
    return sampleData;
  };
  
  // Use sample data for demo or real data when available
  const data = analyticsData || generateSampleData();
  
  // Loading state
  if (isLoading && !currentContent) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <MagicLoader variant="magical" size="medium" message="Loading content..." />
      </Box>
    );
  }
  
  // Error state
  if (error && !currentContent) {
    return (
      <Card 
        level={1} 
        sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.1) }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading Content
        </Typography>
        <Typography variant="body1" paragraph>
          {error}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/content')}
        >
          Back to Content
        </Button>
      </Card>
    );
  }
  
  // Not published warning
  if (currentContent && currentContent.status !== 'published') {
    return (
      <Box>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link 
            component={RouterLink} 
            to="/content"
            color="inherit"
            underline="hover"
          >
            Content
          </Link>
          <Link 
            component={RouterLink} 
            to={`/content/${id}`}
            color="inherit"
            underline="hover"
          >
            {getContentTitle()}
          </Link>
          <Typography color="text.primary">
            Analytics
          </Typography>
        </Breadcrumbs>
        
        <Card 
          level={2}
          sx={{ 
            p: 4, 
            textAlign: 'center',
            bgcolor: alpha(theme.palette.warning.main, 0.05)
          }}
        >
          <Typography variant="h5" gutterBottom color="warning.main">
            Analytics Not Available
          </Typography>
          <Typography variant="body1" paragraph>
            Analytics are only available for published content.
            This content is currently in "{currentContent.status}" status.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate(`/content/${id}`)}
          >
            Back to Content
          </Button>
        </Card>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link 
          component={RouterLink} 
          to="/content"
          color="inherit"
          underline="hover"
        >
          Content
        </Link>
        <Link 
          component={RouterLink} 
          to={`/content/${id}`}
          color="inherit"
          underline="hover"
        >
          {getContentTitle()}
        </Link>
        <Typography color="text.primary">
          Analytics
        </Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Card 
        level={2}
        magical={true}
        sx={{ mb: 4 }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ContentTypeIcon type={currentContent?.type || 'post'} size="medium" />
                <Typography variant="h5" sx={{ ml: 1, fontWeight: 'medium' }}>
                  Analytics: {getContentTitle()}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EntityStatusBadge status={currentContent?.status || 'published'} entityType="content" />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Performance metrics and insights
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(`/content/${id}`)}
            >
              Back to Content
            </Button>
          </Box>
        </Box>
      </Card>
      
      {/* Time Range Selector */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="time-range-label">Time Range</InputLabel>
          <Select
            labelId="time-range-label"
            id="time-range"
            value={timeRange}
            label="Time Range"
            onChange={handleTimeRangeChange}
          >
            <MenuItem value="day">Last 24 Hours</MenuItem>
            <MenuItem value="week">Last 7 Days</MenuItem>
            <MenuItem value="month">Last 30 Days</MenuItem>
            <MenuItem value="quarter">Last 90 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {/* Loading State */}
      {isLoadingAnalytics ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <MagicLoader size="medium" message="Loading analytics data..." />
        </Box>
      ) : (
        <>
          {/* Key Metrics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Impressions"
                value={data.metrics.impressions.total.toLocaleString()}
                change={data.metrics.impressions.change}
                icon={<BarChartIcon />}
                color={theme.palette.primary.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Reach"
                value={data.metrics.reach.total.toLocaleString()}
                change={data.metrics.reach.change}
                icon={<DonutIcon />}
                color={theme.palette.secondary.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Engagement"
                value={data.metrics.engagement.total.toLocaleString()}
                change={data.metrics.engagement.change}
                icon={<TimelineIcon />}
                color={theme.palette.info.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Engagement Rate"
                value={`${data.metrics.engagementRate.total.toFixed(1)}%`}
                change={data.metrics.engagementRate.change}
                icon={<PieChartIcon />}
                color={theme.palette.success.main}
              />
            </Grid>
          </Grid>
          
          {/* Tab Navigation */}
          <Paper sx={{ mb: 4 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="analytics tabs"
              text
              indicator
            >
              <Tab icon={<LineChartIcon />} iconPosition="start" label="Overview" />
              <Tab icon={<PieChartIcon />} iconPosition="start" label="Platform Distribution" />
              <Tab icon={<BarChartIcon />} iconPosition="start" label="Demographics" />
              <Tab icon={<TimelineIcon />} iconPosition="start" label="Engagement Analysis" />
            </Tabs>
          </Paper>
          
          {/* Tab Content */}
          <TabPanel value={tabValue} index={0}>
            <Card level={1} sx={{ mb: 4 }}>
              <CardHeader title="Daily Performance" />
              <Divider />
              <CardContent>
                <DailyEngagementChart data={data.dailyTrend} />
              </CardContent>
            </Card>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card level={1} sx={{ height: '100%' }}>
                  <CardHeader title="Engagement Metrics" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Comments
                        </Typography>
                        <Typography variant="h6">
                          {data.metrics.comments.total.toLocaleString()}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ color: data.metrics.comments.change >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {data.metrics.comments.change >= 0 ? '+' : ''}{data.metrics.comments.change}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Shares
                        </Typography>
                        <Typography variant="h6">
                          {data.metrics.shares.total.toLocaleString()}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ color: data.metrics.shares.change >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {data.metrics.shares.change >= 0 ? '+' : ''}{data.metrics.shares.change}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Clicks
                        </Typography>
                        <Typography variant="h6">
                          {data.metrics.clicks.total.toLocaleString()}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ color: data.metrics.clicks.change >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {data.metrics.clicks.change >= 0 ? '+' : ''}{data.metrics.clicks.change}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card level={1} sx={{ height: '100%' }}>
                  <CardHeader title="Content Performance Summary" />
                  <Divider />
                  <CardContent>
                    <Typography variant="body1" paragraph>
                      This content has received <strong>{data.metrics.impressions.total.toLocaleString()}</strong> impressions
                      and reached <strong>{data.metrics.reach.total.toLocaleString()}</strong> unique users.
                    </Typography>
                    <Typography variant="body1" paragraph>
                      The engagement rate of <strong>{data.metrics.engagementRate.total.toFixed(1)}%</strong> is 
                      {data.metrics.engagementRate.change >= 0 
                        ? ` ${data.metrics.engagementRate.change}% higher than` 
                        : ` ${Math.abs(data.metrics.engagementRate.change)}% lower than`} similar content.
                    </Typography>
                    <Typography variant="body1">
                      Performance is {data.metrics.engagement.change >= 10 
                        ? 'significantly above average' 
                        : data.metrics.engagement.change >= 0 
                          ? 'above average' 
                          : 'below average'}.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Card level={1}>
              <CardHeader title="Platform Distribution" />
              <Divider />
              <CardContent>
                <Typography variant="body1" paragraph>
                  Distribution of engagement across different platforms.
                </Typography>
                <PlatformDistributionChart data={data.platformDistribution} />
              </CardContent>
            </Card>
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <Card level={1}>
              <CardHeader title="Demographic Distribution" />
              <Divider />
              <CardContent>
                <Typography variant="body1" paragraph>
                  Audience demographics by age group and gender.
                </Typography>
                <DemographicBarChart data={data.demographics} />
              </CardContent>
            </Card>
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <Card level={1}>
              <CardHeader title="Engagement Analysis" />
              <Divider />
              <CardContent>
                <Typography variant="body1" paragraph>
                  Comparison of engagement metrics against average performance.
                </Typography>
                <EngagementRadarChart data={data.engagementRadar} />
              </CardContent>
            </Card>
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default ContentAnalytics;